import { useState, useMemo, useRef } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Triangle,
  Crosshair,
  Hand,
  Sun,
  Aperture,
  Timer,
  FilmStrip,
  SunHorizon,
  GridFour,
  GearSix,
  Desktop,
} from "@phosphor-icons/react";
import { Printer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import FundamentalsSection, { content as fundamentalsContent } from "@/components/tools/FundamentalsSection";
import PhotoQuiz, { questions as quizQuestions } from "@/components/tools/PhotoQuiz";
import { usePDFExport } from "@/utils/pdfExport";
import GaleriaShareButton from "@/components/GaleriaShareButton";
import ExifReader from "@/components/tools/ExifReader";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

const SENSOR_COC = {
  "aps-c-1.6": { name: "APS-C (Canon 1.6x)", coc: 0.019, crop: 1.6 },
  "aps-c-1.5": { name: "APS-C (Nikon 1.5x)", coc: 0.02, crop: 1.5 },
  "1/2.3": { name: '1/2.3" (Coolpix)', coc: 0.006, crop: 5.6 },
  "full-frame": { name: "Full Frame (35mm)", coc: 0.03, crop: 1.0 },
};

function ExposureTriangle({ t }) {
  const cards = [
    {
      icon: <Aperture weight="regular" className="w-8 h-8 text-[#F5A524]" />,
      title: t("res_aperture"),
      desc: t("tools_exposure_aperture"),
      examples: ["f/1.8", "f/4", "f/8", "f/16"],
      labels: ["Wide open", "", "", "Closed down"],
      effect: "Depth of Field",
      effectRange: ["Shallow", "Deep"],
    },
    {
      icon: <Timer weight="regular" className="w-8 h-8 text-[#F5A524]" />,
      title: t("res_shutter"),
      desc: t("tools_exposure_shutter"),
      examples: ["1/2000", "1/500", "1/60", "1/15"],
      labels: ["Fast", "", "", "Slow"],
      effect: "Motion",
      effectRange: ["Frozen", "Blurred"],
    },
    {
      icon: <FilmStrip weight="regular" className="w-8 h-8 text-[#F5A524]" />,
      title: t("res_iso"),
      desc: t("tools_exposure_iso"),
      examples: ["100", "400", "1600", "6400"],
      labels: ["Clean", "", "", "Noisy"],
      effect: "Noise",
      effectRange: ["Clean", "Grainy"],
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <Triangle weight="fill" className="w-6 h-6 text-[#F5A524]" />
        <div>
          <h3 className="font-['Outfit'] text-lg text-white font-medium">
            {t("tools_exposure_title")}
          </h3>
          <p className="text-[#71717A] text-sm">{t("tools_exposure_desc")}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((card, i) => (
          <motion.div
            key={i}
            variants={item}
            className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5 hover:border-white/20 transition-colors"
            data-testid={`exposure-card-${i}`}
          >
            <div className="flex items-center gap-2 mb-3">
              {card.icon}
              <span className="text-white font-medium">{card.title}</span>
            </div>
            <p className="text-[#A1A1AA] text-sm mb-4 leading-relaxed">
              {card.desc}
            </p>
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-[#71717A]">
                <span>{card.effectRange[0]}</span>
                <span className="text-[#F5A524]">{card.effect}</span>
                <span>{card.effectRange[1]}</span>
              </div>
              <div className="flex gap-1">
                {card.examples.map((ex, j) => (
                  <div
                    key={j}
                    className="flex-1 text-center py-1.5 rounded-md bg-[#121214] font-['JetBrains_Mono'] text-xs text-white"
                  >
                    {ex}
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xs text-[#71717A]">
                <span>{card.labels[0]}</span>
                <span>{card.labels[3]}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function DOFCalculator({ t }) {
  const [focal, setFocal] = useState("50");
  const [aperture, setAperture] = useState("2.8");
  const [distance, setDistance] = useState("3");
  const [sensor, setSensor] = useState("aps-c-1.6");

  const result = useMemo(() => {
    const f = parseFloat(focal) || 50;
    const N = parseFloat(aperture) || 2.8;
    const d = parseFloat(distance) || 3;
    const s = SENSOR_COC[sensor] || SENSOR_COC["aps-c-1.6"];
    const c = s.coc;

    const d_mm = d * 1000;
    const H = (f * f) / (N * c) + f;
    const nearDist = (H * d_mm) / (H + (d_mm - f));
    const farDist = d_mm < H ? (H * d_mm) / (H - (d_mm - f)) : Infinity;
    const totalDof = farDist === Infinity ? Infinity : farDist - nearDist;

    return {
      near: (nearDist / 1000).toFixed(2),
      far: farDist === Infinity ? null : (farDist / 1000).toFixed(2),
      total: totalDof === Infinity ? null : (totalDof / 1000).toFixed(2),
    };
  }, [focal, aperture, distance, sensor]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <Crosshair weight="fill" className="w-6 h-6 text-[#F5A524]" />
        <div>
          <h3 className="font-['Outfit'] text-lg text-white font-medium">
            {t("tools_dof_title")}
          </h3>
          <p className="text-[#71717A] text-sm">{t("tools_dof_desc")}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("tools_dof_focal")}
          </Label>
          <Input
            type="number"
            min="1"
            max="800"
            value={focal}
            onChange={(e) => setFocal(e.target.value)}
            className="bg-[#0A0A0A] border-white/10 text-white"
            data-testid="dof-focal"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("tools_dof_aperture")}
          </Label>
          <Input
            type="number"
            min="1"
            max="32"
            step="0.1"
            value={aperture}
            onChange={(e) => setAperture(e.target.value)}
            className="bg-[#0A0A0A] border-white/10 text-white"
            data-testid="dof-aperture"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("tools_dof_distance")} ({t("tools_dof_meters")})
          </Label>
          <Input
            type="number"
            min="0.1"
            max="1000"
            step="0.1"
            value={distance}
            onChange={(e) => setDistance(e.target.value)}
            className="bg-[#0A0A0A] border-white/10 text-white"
            data-testid="dof-distance"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("tools_dof_sensor")}
          </Label>
          <Select value={sensor} onValueChange={setSensor}>
            <SelectTrigger
              className="bg-[#0A0A0A] border-white/10 text-white text-xs"
              data-testid="dof-sensor"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-white/10">
              {Object.entries(SENSOR_COC).map(([key, val]) => (
                <SelectItem key={key} value={key}>
                  {val.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5">
        <h4 className="text-[#71717A] text-xs uppercase tracking-[0.15em] mb-3">
          {t("tools_dof_result")}
        </h4>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-[#71717A] text-xs mb-1">{t("tools_dof_near")}</div>
            <div
              className="font-['JetBrains_Mono'] text-xl text-white"
              data-testid="dof-near-result"
            >
              {result.near}
              <span className="text-[#71717A] text-sm ml-1">
                {t("tools_dof_meters")}
              </span>
            </div>
          </div>
          <div>
            <div className="text-[#71717A] text-xs mb-1">{t("tools_dof_far")}</div>
            <div
              className="font-['JetBrains_Mono'] text-xl text-white"
              data-testid="dof-far-result"
            >
              {result.far || t("tools_dof_infinity")}
              {result.far && (
                <span className="text-[#71717A] text-sm ml-1">
                  {t("tools_dof_meters")}
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[#71717A] text-xs mb-1">{t("tools_dof_total")}</div>
            <div
              className="font-['JetBrains_Mono'] text-xl text-[#F5A524]"
              data-testid="dof-total-result"
            >
              {result.total || t("tools_dof_infinity")}
              {result.total && (
                <span className="text-[#71717A] text-sm ml-1">
                  {t("tools_dof_meters")}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HandheldCalculator({ t }) {
  const [focal, setFocal] = useState("50");
  const [crop, setCrop] = useState("1.6");

  const minSpeed = useMemo(() => {
    const f = parseFloat(focal) || 50;
    const c = parseFloat(crop) || 1.6;
    const equiv = f * c;
    const speed = Math.round(equiv);
    return `1/${speed}`;
  }, [focal, crop]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <Hand weight="fill" className="w-6 h-6 text-[#F5A524]" />
        <div>
          <h3 className="font-['Outfit'] text-lg text-white font-medium">
            {t("tools_handheld_title")}
          </h3>
          <p className="text-[#71717A] text-sm">{t("tools_handheld_desc")}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("tools_dof_focal")}
          </Label>
          <Input
            type="number"
            min="1"
            max="800"
            value={focal}
            onChange={(e) => setFocal(e.target.value)}
            className="bg-[#0A0A0A] border-white/10 text-white"
            data-testid="handheld-focal"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("tools_crop_factor")}
          </Label>
          <Select value={crop} onValueChange={setCrop}>
            <SelectTrigger
              className="bg-[#0A0A0A] border-white/10 text-white"
              data-testid="handheld-crop"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-white/10">
              <SelectItem value="1.0">1.0x (Full Frame)</SelectItem>
              <SelectItem value="1.5">1.5x (Nikon APS-C)</SelectItem>
              <SelectItem value="1.6">1.6x (Canon APS-C)</SelectItem>
              <SelectItem value="5.6">5.6x (1/2.3" sensor)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5 text-center">
        <div className="text-[#71717A] text-xs uppercase tracking-[0.15em] mb-2">
          {t("tools_handheld_result")}
        </div>
        <div
          className="font-['JetBrains_Mono'] text-4xl text-[#F5A524] setting-value-glow"
          data-testid="handheld-result"
        >
          {minSpeed}
        </div>
        <p className="text-[#71717A] text-xs mt-2">
          {focal}mm x {crop} = {Math.round((parseFloat(focal) || 50) * (parseFloat(crop) || 1.6))}mm
          equivalent
        </p>
      </div>
    </div>
  );
}

function Sunny16Reference({ t }) {
  const scenes = [
    {
      label: t("tools_sunny16_bright"),
      aperture: "f/16",
      shutter: "1/100",
      iso: "100",
      color: "#F5A524",
    },
    {
      label: t("tools_sunny16_cloudy"),
      aperture: "f/11",
      shutter: "1/100",
      iso: "100",
      color: "#A1A1AA",
    },
    {
      label: t("tools_sunny16_shade"),
      aperture: "f/8",
      shutter: "1/100",
      iso: "100",
      color: "#6B7280",
    },
    {
      label: t("tools_sunny16_sunset"),
      aperture: "f/5.6",
      shutter: "1/100",
      iso: "100",
      color: "#F97316",
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <Sun weight="fill" className="w-6 h-6 text-[#F5A524]" />
        <div>
          <h3 className="font-['Outfit'] text-lg text-white font-medium">
            {t("tools_sunny16_title")}
          </h3>
          <p className="text-[#71717A] text-sm">{t("tools_sunny16_desc")}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {scenes.map((scene, i) => (
          <div
            key={i}
            className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 text-center hover:border-white/20 transition-colors"
            data-testid={`sunny16-card-${i}`}
          >
            <div
              className="w-2 h-2 rounded-full mx-auto mb-2"
              style={{ backgroundColor: scene.color }}
            />
            <div className="text-[#A1A1AA] text-xs mb-2">{scene.label}</div>
            <div className="font-['JetBrains_Mono'] text-lg text-white mb-1">
              {scene.aperture}
            </div>
            <div className="font-['JetBrains_Mono'] text-xs text-[#71717A]">
              {scene.shutter} | ISO {scene.iso}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const RECIPROCITY_FILMS = [
  { key: "ilford", label: "Ilford (HP5, FP4, Delta, Pan F)", p: 1.31 },
  { key: "trix", label: "Kodak Tri-X 400", p: 1.28 },
  { key: "tmax", label: "Kodak T-Max 100/400 (low failure)", p: 1.0 },
  { key: "fomapan", label: "Fomapan 100/200/400 (heavy)", p: 1.43 },
  { key: "generic", label: "Generic / unknown B&W", p: 1.33 },
];

function fmtSeconds(s) {
  if (s >= 60) {
    const m = Math.floor(s / 60);
    const rem = Math.round(s % 60);
    return `${m}m ${rem}s`;
  }
  return `${s < 10 ? s.toFixed(1) : Math.round(s)}s`;
}

function ReciprocityCalculator({ t, lang }) {
  const [metered, setMetered] = useState("8");
  const [filmKey, setFilmKey] = useState("ilford");
  const film = RECIPROCITY_FILMS.find((f) => f.key === filmKey) || RECIPROCITY_FILMS[0];

  const result = useMemo(() => {
    const m = parseFloat(metered) || 0;
    if (m <= 0) return { corrected: 0, stops: 0, needed: false };
    if (m < 1 || film.p === 1.0) return { corrected: m, stops: 0, needed: false };
    const corrected = Math.pow(m, film.p);
    const stops = Math.log2(corrected / m);
    return { corrected, stops, needed: true };
  }, [metered, film]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <Timer weight="fill" className="w-6 h-6 text-[#F5A524]" />
        <div>
          <h3 className="font-['Outfit'] text-lg text-white font-medium">
            {lang === "es" ? "Fallo de Reciprocidad" : "Reciprocity Failure"}
          </h3>
          <p className="text-[#71717A] text-sm">
            {lang === "es"
              ? "Corrige largas exposiciones en película. Ingresa el tiempo medido por el fotómetro."
              : "Correct long film exposures. Enter your meter's recommended time."}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {lang === "es" ? "Tiempo medido (s)" : "Metered time (s)"}
          </Label>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={metered}
            onChange={(e) => setMetered(e.target.value)}
            className="bg-[#0A0A0A] border-white/10 text-white"
            data-testid="recip-metered"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {lang === "es" ? "Película" : "Film stock"}
          </Label>
          <Select value={filmKey} onValueChange={setFilmKey}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white text-xs" data-testid="recip-film">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-white/10">
              {RECIPROCITY_FILMS.map((f) => (
                <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5 text-center">
        <div className="text-[#71717A] text-xs uppercase tracking-[0.15em] mb-2">
          {lang === "es" ? "Exposición corregida" : "Corrected exposure"}
        </div>
        <div className="font-['JetBrains_Mono'] text-4xl text-[#F5A524] setting-value-glow" data-testid="recip-result">
          {fmtSeconds(result.corrected)}
        </div>
        <p className="text-[#71717A] text-xs mt-2">
          {result.needed
            ? `+${result.stops.toFixed(1)} ${lang === "es" ? "pasos extra" : "extra stops"} · ${lang === "es" ? "fórmula" : "formula"}: t^${film.p}`
            : lang === "es"
            ? "No se necesita corrección (< 1s o película de baja reciprocidad)."
            : "No correction needed (< 1s or low-reciprocity film)."}
        </p>
      </div>
      <p className="text-[#52525B] text-xs italic leading-relaxed">
        {lang === "es"
          ? "Aproximado — consulta la hoja de datos de tu película para tablas exactas. La película de color además puede virar de tono y requerir filtros."
          : "Approximate — check your film's datasheet for exact tables. Color film may also shift color and need filtration."}
      </p>
    </div>
  );
}

const SUNNY_SCENES = [
  { key: "bright", aperture: "f/16", en: "Bright sun, hard shadows", es: "Sol fuerte, sombras duras" },
  { key: "haze", aperture: "f/11", en: "Slight haze / soft shadows", es: "Bruma leve / sombras suaves" },
  { key: "overcast", aperture: "f/8", en: "Overcast", es: "Nublado" },
  { key: "heavy", aperture: "f/5.6", en: "Heavy overcast", es: "Muy nublado" },
  { key: "shade", aperture: "f/4", en: "Open shade / sunset", es: "Sombra abierta / atardecer" },
];

const STD_DENOMS = [1, 2, 4, 8, 15, 30, 60, 125, 250, 500, 1000, 2000, 4000, 8000];

function nearestShutter(iso) {
  let closest = STD_DENOMS[0];
  let best = Infinity;
  for (const d of STD_DENOMS) {
    const diff = Math.abs(d - iso);
    if (diff < best) { best = diff; closest = d; }
  }
  return `1/${closest}`;
}

function Sunny16Helper({ t, lang }) {
  const [sceneKey, setSceneKey] = useState("bright");
  const [iso, setIso] = useState("400");
  const scene = SUNNY_SCENES.find((s) => s.key === sceneKey) || SUNNY_SCENES[0];
  const shutter = useMemo(() => nearestShutter(parseInt(iso) || 400), [iso]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <SunHorizon weight="fill" className="w-6 h-6 text-[#F5A524]" />
        <div>
          <h3 className="font-['Outfit'] text-lg text-white font-medium">
            {lang === "es" ? "Ayudante Sunny-16 / Fotómetro" : "Sunny-16 / Hand-Meter Helper"}
          </h3>
          <p className="text-[#71717A] text-sm">
            {lang === "es"
              ? "Sin fotómetro: elige la escena y el ISO para tu ajuste de exposición."
              : "No meter? Pick the scene and ISO for your exposure setting."}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {lang === "es" ? "Escena" : "Scene"}
          </Label>
          <Select value={sceneKey} onValueChange={setSceneKey}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white text-xs" data-testid="sunny-scene">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-white/10">
              {SUNNY_SCENES.map((s) => (
                <SelectItem key={s.key} value={s.key}>{lang === "es" ? s.es : s.en}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">ISO / ASA</Label>
          <Select value={iso} onValueChange={setIso}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="sunny-iso">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-white/10">
              {[50, 100, 200, 400, 800, 1600, 3200].map((v) => (
                <SelectItem key={v} value={String(v)}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5">
        <div className="text-[#71717A] text-xs uppercase tracking-[0.15em] mb-3 text-center">
          {lang === "es" ? "Ajuste recomendado" : "Recommended setting"}
        </div>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-[#71717A] text-xs mb-1">{t("res_aperture")}</div>
            <div className="font-['JetBrains_Mono'] text-2xl text-[#F5A524]" data-testid="sunny-aperture">{scene.aperture}</div>
          </div>
          <div>
            <div className="text-[#71717A] text-xs mb-1">{t("res_shutter")}</div>
            <div className="font-['JetBrains_Mono'] text-2xl text-white" data-testid="sunny-shutter">{shutter}</div>
          </div>
          <div>
            <div className="text-[#71717A] text-xs mb-1">ISO</div>
            <div className="font-['JetBrains_Mono'] text-2xl text-white" data-testid="sunny-iso-result">{iso}</div>
          </div>
        </div>
      </div>
      <p className="text-[#52525B] text-xs italic leading-relaxed">
        {lang === "es"
          ? "Regla: velocidad ≈ 1/ISO, luego ajusta la apertura según la luz. Usa la velocidad marcada más cercana en tu cámara."
          : "Rule: shutter ≈ 1/ISO, then set aperture for the light. Use the closest marked speed on your camera."}
      </p>
      <GaleriaShareButton testId="sunny-share-galeria-btn" />
    </div>
  );
}

export default function ToolsPage() {
  const { t, lang } = useTranslation();
  const printRef = useRef(null);

  const { ready, generating, download } = usePDFExport(
    printRef,
    () => `photography-tools-${lang}.pdf`,
    [lang]
  );

  const sunny16 = [
    { label: t("tools_sunny16_bright"), aperture: "f/16", shutter: "1/100", iso: "100" },
    { label: t("tools_sunny16_cloudy"), aperture: "f/11", shutter: "1/100", iso: "100" },
    { label: t("tools_sunny16_shade"), aperture: "f/8", shutter: "1/100", iso: "100" },
    { label: t("tools_sunny16_sunset"), aperture: "f/5.6", shutter: "1/100", iso: "100" },
  ];

  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black"
      data-testid="tools-page"
    >
      {/* Screen-only content */}
      <div className="print:hidden">
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="space-y-8"
      >
        <motion.div variants={item} className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
              {t("tools_title")}
            </h1>
            <p className="text-[#71717A] text-base">{t("tools_subtitle")}</p>
          </div>
          <Button onClick={download} disabled={!ready} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60" data-testid="tools-download-btn">
            <Printer className="w-4 h-4 mr-1.5" />
            {generating ? (lang === "es" ? "Generando..." : "Generating...") : (lang === "es" ? "Descargar como PDF" : "Download as PDF")}
          </Button>
        </motion.div>

        <motion.div
          variants={item}
          className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
        >
          <ExposureTriangle t={t} />
        </motion.div>

        <motion.div
          variants={item}
          className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
        >
          <DOFCalculator t={t} />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            variants={item}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
          >
            <HandheldCalculator t={t} />
          </motion.div>

          <motion.div
            variants={item}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
          >
            <Sunny16Reference t={t} />
          </motion.div>
        </div>

        {/* Film-specific calculators */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            variants={item}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
          >
            <Sunny16Helper t={t} lang={lang} />
          </motion.div>

          <motion.div
            variants={item}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
          >
            <ReciprocityCalculator t={t} lang={lang} />
          </motion.div>
        </div>

        {/* EXIF Reader (browser-only) */}
        <motion.div
          variants={item}
          className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
        >
          <ExifReader />
        </motion.div>

        {/* Photography Fundamentals */}
        <FundamentalsSection />

        {/* Interactive Quiz */}
        <PhotoQuiz />
      </motion.div>
      </div>

      {/* Print-only clean PDF layout */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4" data-testid="tools-print-view">
        <h1 className="text-3xl font-bold text-center mb-1">{t("tools_title")}</h1>
        <p className="text-center text-gray-500 mb-6">{t("tools_subtitle")}</p>

        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">{t("tools_exposure_title")}</h2>
        <p className="text-sm text-gray-700 mb-3">{t("tools_exposure_desc")}</p>
        <div className="mb-6 space-y-2">
          <p className="text-sm"><strong>{t("res_aperture")}:</strong> {t("tools_exposure_aperture")}</p>
          <p className="text-sm"><strong>{t("res_shutter")}:</strong> {t("tools_exposure_shutter")}</p>
          <p className="text-sm"><strong>{t("res_iso")}:</strong> {t("tools_exposure_iso")}</p>
        </div>

        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">{t("tools_dof_title")}</h2>
        <p className="text-sm text-gray-700 mb-3">{t("tools_dof_desc")}</p>
        <p className="text-sm mb-6">
          {lang === "es"
            ? "Formula: H = (f * f) / (N * c) + f, donde H es la distancia hiperfocal, f es la distancia focal, N es el número f, y c es el círculo de confusión del sensor."
            : "Formula: H = (f * f) / (N * c) + f, where H is the hyperfocal distance, f is focal length, N is the f-number, and c is the sensor's circle of confusion."}
        </p>

        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">{t("tools_handheld_title")}</h2>
        <p className="text-sm text-gray-700 mb-3">{t("tools_handheld_desc")}</p>
        <p className="text-sm mb-6">
          {lang === "es"
            ? "Regla: Velocidad mínima = 1 / (distancia focal * factor de recorte). Ej: 50mm en un sensor APS-C Canon (1.6x) = 1/80s mínimo."
            : "Rule: Minimum shutter = 1 / (focal length * crop factor). Example: 50mm on a Canon APS-C body (1.6x) = 1/80s minimum."}
        </p>

        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">{t("tools_sunny16_title")}</h2>
        <p className="text-sm text-gray-700 mb-3">{t("tools_sunny16_desc")}</p>
        <table className="w-full text-sm border-collapse mb-6">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 p-2 text-left">{lang === "es" ? "Escena" : "Scene"}</th>
              <th className="border border-gray-300 p-2 text-left">{t("res_aperture")}</th>
              <th className="border border-gray-300 p-2 text-left">{t("res_shutter")}</th>
              <th className="border border-gray-300 p-2 text-left">ISO</th>
            </tr>
          </thead>
          <tbody>
            {sunny16.map((s, i) => (
              <tr key={i}>
                <td className="border border-gray-300 p-2">{s.label}</td>
                <td className="border border-gray-300 p-2 font-mono">{s.aperture}</td>
                <td className="border border-gray-300 p-2 font-mono">{s.shutter}</td>
                <td className="border border-gray-300 p-2 font-mono">{s.iso}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <FundamentalsPrintBlock lang={lang} />
        <QuizPrintBlock lang={lang} />

        <p className="text-center text-xs text-gray-400 mt-6">www.dslradvisor.com</p>
      </div>
    </div>
  );
}

// --- Print-only Fundamentals block ---------------------------------------
function FundamentalsPrintBlock({ lang }) {
  const c = fundamentalsContent[lang] || fundamentalsContent.en;
  return (
    <div className="mb-6">
      <h2 className="text-xl font-bold mb-1 border-b-2 border-gray-200 pb-2">
        {c.fundamentals_heading}
      </h2>
      <p className="text-xs text-gray-600 italic mb-3">{c.fundamentals_sub}</p>

      <h3 className="text-base font-semibold mt-4 mb-1">{c.rot_title}</h3>
      <p className="text-sm text-gray-700 mb-2">{c.rot_desc}</p>
      <ul className="list-disc ml-5 mb-3">
        <li className="text-xs text-gray-700">{c.rot_tip1}</li>
        <li className="text-xs text-gray-700">{c.rot_tip2}</li>
        <li className="text-xs text-gray-700">{c.rot_tip3}</li>
        <li className="text-xs text-gray-700">{c.rot_tip4}</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-1">{c.lc_title}</h3>
      <p className="text-sm text-gray-700 mb-2">{c.lc_desc}</p>
      <table className="w-full text-xs border-collapse mb-2">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-1 text-left">Kelvin</th>
            <th className="border border-gray-300 p-1 text-left">
              {lang === "es" ? "Fuente" : "Source"}
            </th>
          </tr>
        </thead>
        <tbody>
          {c.lc_items.map((it, i) => (
            <tr key={i}>
              <td className="border border-gray-300 p-1 font-mono">{it.k}</td>
              <td className="border border-gray-300 p-1">{it.label}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-gray-700 mb-1"><strong>Golden Hour:</strong> {c.lc_golden}</p>
      <p className="text-xs text-gray-700 mb-3"><strong>Blue Hour:</strong> {c.lc_blue}</p>

      <h3 className="text-base font-semibold mt-4 mb-1">{c.comp_title}</h3>
      <p className="text-sm text-gray-700 mb-2">{c.comp_desc}</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3">
        {c.comp_cards.map((card, i) => (
          <p key={i} className="text-xs text-gray-700">
            <strong>{card.name}:</strong> {card.tip}
          </p>
        ))}
      </div>

      <h3 className="text-base font-semibold mt-4 mb-1">{c.tech_title}</h3>
      <p className="text-sm text-gray-700 mb-2">{c.tech_desc}</p>

      <p className="text-sm font-semibold mb-1">{c.tech_focus_title}</p>
      <ul className="list-disc ml-5 mb-2">
        {c.tech_focus.map((f, i) => (
          <li key={i} className="text-xs text-gray-700">
            <strong>{f.mode}:</strong> {f.use}
          </li>
        ))}
      </ul>
      <p className="text-sm font-semibold mb-1">{c.tech_meter_title}</p>
      <ul className="list-disc ml-5 mb-2">
        {c.tech_meter.map((m, i) => (
          <li key={i} className="text-xs text-gray-700">
            <strong>{m.mode}:</strong> {m.use}
          </li>
        ))}
      </ul>
      <p className="text-sm font-semibold mb-1">{c.tech_wb_title}</p>
      <ul className="list-disc ml-5 mb-3">
        {c.tech_wb.map((w, i) => (
          <li key={i} className="text-xs text-gray-700">
            <strong>{w.preset}:</strong> {w.when}
          </li>
        ))}
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-1">{c.pp_title}</h3>
      <p className="text-sm text-gray-700 mb-2">{c.pp_desc}</p>
      <ol className="list-decimal ml-5 mb-2">
        {c.pp_steps.map((s, i) => (
          <li key={i} className="text-xs text-gray-700 mb-1">
            <strong>{s.step}:</strong> {s.detail}
          </li>
        ))}
      </ol>
    </div>
  );
}

// --- Print-only Quiz block ------------------------------------------------
function QuizPrintBlock({ lang }) {
  const items = quizQuestions[lang] || quizQuestions.en;
  const heading = lang === "es" ? "Quiz de Conocimiento" : "Knowledge Quiz";
  const answerLabel = lang === "es" ? "Respuesta" : "Answer";
  return (
    <div className="mb-6">
      <h2 className="text-xl font-bold mb-1 border-b-2 border-gray-200 pb-2">
        {heading}
      </h2>
      <p className="text-xs text-gray-600 italic mb-3">
        {lang === "es"
          ? "Pon a prueba tus conocimientos. Las respuestas correctas están marcadas."
          : "Test your knowledge. Correct answers are marked."}
      </p>
      <ol className="list-decimal ml-5 space-y-3">
        {items.map((item, i) => (
          <li key={i} className="text-sm text-gray-800">
            <p className="font-semibold mb-1">{item.q}</p>
            <ul className="list-none ml-0 mb-1">
              {item.opts.map((opt, j) => {
                const isCorrect = j === item.correct;
                return (
                  <li
                    key={j}
                    className={`text-xs ${isCorrect ? "font-semibold text-green-700" : "text-gray-700"}`}
                  >
                    {String.fromCharCode(65 + j)}. {opt} {isCorrect ? "✓" : ""}
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-gray-600 italic">
              <strong>{answerLabel}:</strong> {item.explain}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}
